import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, Heart, MessageCircle, Share2, MoreHorizontal, Loader2, Users, Star, LayoutGrid, Plus, PenSquare, ArrowLeft, X, Send, ShieldAlert, Trash2 } from 'lucide-react';
import { StadiumPostModal } from './StadiumPostModal';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { communityService } from '../../services/communityService';
import { favoriteService } from '../../services/favoriteService';
import { fetchTeamInfo } from '../../services/footballService';
import { getTeamName } from '../../services/teamService';
import { toggleNewsLike } from '../../services/newsCommentService';
import { StadiumPost, RouteState, CommentWithUser } from '../../types';
import { EmptyState } from '../Common/EmptyState';
import { ImageWithFallback } from '../Common/ImageWithFallback';
import { ConfirmModal } from '../Common/ConfirmModal';
import { ReportModal } from '../Common/ReportModal';
import { NavBar } from '../Layout/NavBar';
import { Tab } from '../../types';
import { SidebarSearch } from '../Common/SidebarSearch';
import { fetchNewsComments, createNewsComment, deleteNewsComment, reportNewsComment, likeNewsComment, unlikeNewsComment } from '../../services/newsCommentService';
import { useToast } from '../../context/ToastContext';
import { calculateTimeAgo } from '../../services/formatService';

interface PhantomStadiumProps {
  route?: RouteState;
  refreshTrigger?: number;
  onLoadingChange?: (loading: boolean) => void;
  isPostModalOpenExternal?: boolean;
  onPostModalCloseExternal?: () => void;
}

const StadiumPostItem: React.FC<{
  post: StadiumPost;
  lang: any;
  handleLike: (postId: string, e: React.MouseEvent) => void;
  onCommentClick: () => void;
}> = ({ post, lang, handleLike, onCommentClick }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const shouldShowExpand = post.content && (post.content.split('\n').length > 3 || (post.content?.length || 0) > 120);
  const isMine = user?.id === post.user_id;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleDelete = async () => {
    try {
      await communityService.deletePost(post.id);
      showToast('success', t(lang, 'deleteSuccess'));
      // Reload posts or call an onDelete callback
      window.location.reload();
    } catch (error) {
      console.error("Failed to delete post:", error);
      showToast('error', t(lang, 'deleteFailed'));
    }
  };

  const handleReport = async (reason: string, details?: string) => {
    if (!user) {
      (window as any).navigateTo?.('/login');
      return;
    }
    try {
      await communityService.reportPost(post.id, reason);
      showToast('success', t(lang, 'reportSubmitted'));
    } catch (e) {
      showToast('error', t(lang, 'alreadyReported'));
    }
  };

  return (
    <div className="bg-white border-b border-slate-50 py-6">
      {/* Header */}
      <div className="px-1 flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-100 bg-slate-50 shrink-0">
              <img 
                src={post.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.name || 'U')}&background=random`} 
                alt="" 
                className="w-full h-full object-cover"
              />
            </div>
            {post.team_id && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full p-[2px] shadow-sm border border-slate-100">
                <ImageWithFallback 
                  src={`https://media.api-sports.io/football/teams/${post.team_id}.png`}
                  alt=""
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-bold text-slate-900">{post.user?.name}</span>
            </div>
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              {new Date(post.created_at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>
        <div className="relative" ref={menuRef}>
          <button 
            className="w-8 h-8 flex items-center justify-center rounded-full text-slate-300 hover:text-slate-900 transition-colors active:bg-slate-50 relative z-10"
            onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }}
          >
            <MoreHorizontal size={18} />
          </button>
          {isMenuOpen && (
            <div className="absolute top-full right-0 mt-1 w-32 bg-white border border-slate-100 rounded-xl shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-1 flex flex-col">
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setShowReportModal(true); }}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-tight text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <ShieldAlert size={14} className="text-rose-500" />
                  <span>{t(lang, 'report')}</span>
                </button>
                {isMine && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setShowDeleteConfirm(true); }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-tight text-rose-500 hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 size={14} />
                    <span>{t(lang, 'delete')}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-1 mb-4">
        <p className={`text-[15px] text-slate-800 leading-relaxed whitespace-pre-wrap ${!isExpanded && shouldShowExpand ? 'line-clamp-2' : ''}`}>
          {post.content}
        </p>
        {!isExpanded && shouldShowExpand && (
          <button 
            onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
            className="text-rose-500 text-[13px] font-bold mt-1"
          >
            {t(lang, 'viewMore') || 'See more'}
          </button>
        )}
      </div>

      {/* Image */}
      {post.image_url && (
        <div className="px-1 mb-4">
          <div className="aspect-square rounded-[20px] overflow-hidden bg-slate-50 border border-slate-100">
            <img 
              src={post.image_url} 
              alt="" 
              className="w-full h-full object-cover" 
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-1 flex items-center gap-6">
        <button 
          className={`flex items-center gap-2 transition-colors active:scale-90 ${
            post.is_liked ? 'text-rose-500' : 'text-slate-400'
          }`}
          onClick={(e) => handleLike(post.id, e)}
        >
          <Heart size={20} className={post.is_liked ? 'fill-rose-500' : ''} strokeWidth={2.5} />
          <span className="text-[13px] font-black">{post.likes_count}</span>
        </button>
        
        <button 
          className="flex items-center gap-2 text-slate-400 active:bg-slate-50 rounded-lg transition-colors"
          onClick={(e) => { e.stopPropagation(); onCommentClick(); }}
        >
          <MessageCircle size={20} strokeWidth={2.5} />
          <span className="text-[13px] font-black">{post.comments_count}</span>
        </button>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t(lang, 'deleteConfirm')}
        description={t(lang, 'deleteConfirmMessage')}
        confirmLabel={t(lang, 'delete')}
        cancelLabel={t(lang, 'cancel')}
      />

      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onConfirm={handleReport}
          lang={lang}
        />
      )}
    </div>
  );
};

export const PhantomStadium: React.FC<PhantomStadiumProps> = ({ 
  route, 
  refreshTrigger, 
  onLoadingChange,
  isPostModalOpenExternal,
  onPostModalCloseExternal
}) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [posts, setPosts] = useState<StadiumPost[] | null>(null);
  const [favoriteTeams, setFavoriteTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [hasNewPosts, setHasNewPosts] = useState<Record<string, boolean>>({});
  const [localSelectedTeamId, setLocalSelectedTeamId] = useState<string | undefined>(route?.id);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);

  const selectedTeamId = localSelectedTeamId || route?.id;

  // 댓글 모달 관련 상태
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activePost, setActivePost] = useState<StadiumPost | null>(null);
  const [comments, setComments] = useState<(CommentWithUser & { is_liked?: boolean })[]>([]);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState<CommentWithUser | null>(null);
  const [activeCommentMenuId, setActiveCommentMenuId] = useState<string | number | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<string | number | null>(null);
  const [commentToReport, setCommentToReport] = useState<string | number | null>(null);
  const [expandedParents, setExpandedParents] = useState<Record<string | number, boolean>>({});
  const commentMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (commentMenuRef.current && !commentMenuRef.current.contains(e.target as Node)) {
        setActiveCommentMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStadiumData = useCallback(async (isInitial = true) => {
    if (isInitial) {
      setLoading(true);
      setPostsLoading(true);
    } else {
      setPostsLoading(true);
    }
    try {
      const favorites = await favoriteService.getFavoriteTeams();
      const favoriteTeamIds = favorites.map(f => f.team_id);

      // Fetch details for all favorite teams
      const teamDetails = await Promise.all(
          favoriteTeamIds.map(async (id) => {
              try {
                  const details = await fetchTeamInfo(id);
                  return details ? { 
                    id: details.team.id,
                    name: details.team.name,
                    logo: details.team.logo
                  } : null;
              } catch (e) {
                  return null;
              }
          })
      );
      
      const filteredTeams = teamDetails.filter(Boolean);
      setFavoriteTeams(filteredTeams);

      // Check for new posts for each team (mocking logic: teams with posts in last 24h)
      const newPostStatus: Record<string, boolean> = {};
      const allPosts = await communityService.getStadiumPosts(100);
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
      
      filteredTeams.forEach(team => {
          newPostStatus[team.id] = allPosts.some(p => p.team_id === String(team.id) && new Date(p.created_at) > yesterday);
      });
      setHasNewPosts(newPostStatus);

      // If no team is selected, but user has favorites, auto-select the first favorite team
      if (!selectedTeamId && filteredTeams.length > 0) {
        (window as any).navigateTo?.(`/community/stadium/${filteredTeams[0].id}`);
        return;
      }

      if (favoriteTeamIds.length === 0) {
        setPosts([]);
        setFavoriteTeams([]);
        setLoading(false);
        setPostsLoading(false);
        return;
      }

      const fetchedPosts = await communityService.getStadiumPosts(40, selectedTeamId ? [selectedTeamId] : favoriteTeamIds);
      const visiblePosts = (fetchedPosts || []).filter(post => !post.is_hidden);
      setPosts(visiblePosts);
    } catch (error) {

      console.error("Failed to load stadium posts:", error);
    } finally {
      // Ensure state updates are batched or at least sequential to prevent flickering
      setPostsLoading(false);
      if (isInitial) setLoading(false);
    }
  }, [selectedTeamId, lang]);

  useEffect(() => {
    // Only set whole page loading on first mount or if team list is empty
    const isFirstLoad = favoriteTeams.length === 0;
    loadStadiumData(isFirstLoad);
  }, [selectedTeamId, loadStadiumData]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadStadiumData(false);
    }
  }, [refreshTrigger, loadStadiumData]);

  useEffect(() => {
    if (isPostModalOpenExternal) {
      if (!user) {
        (window as any).navigateTo?.('/login');
        onPostModalCloseExternal?.();
        return;
      }
      setIsPostModalOpen(true);
      onPostModalCloseExternal?.();
    }
  }, [isPostModalOpenExternal, user, onPostModalCloseExternal]);

  useEffect(() => {
    onLoadingChange?.(postsLoading);
  }, [postsLoading, onLoadingChange]);

  const handleLike = async (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
        (window as any).navigateTo?.('/login');
        return;
    }

    // Optimistic Update
    setPosts(prev => prev ? prev.map(p => {
        if (p.id === postId) {
            const newIsLiked = !p.is_liked;
            return {
                ...p,
                is_liked: newIsLiked,
                likes_count: p.likes_count + (newIsLiked ? 1 : -1)
            };
        }
        return p;
    }) : null);

    try {
        await toggleNewsLike(postId);
    } catch (error) {
        console.error("Failed to toggle like:", error);
        // Revert on error
        setPosts(prev => prev ? prev.map(p => {
            if (p.id === postId) {
                const revertedIsLiked = !p.is_liked;
                return {
                    ...p,
                    is_liked: revertedIsLiked,
                    likes_count: p.likes_count + (revertedIsLiked ? 1 : -1)
                };
            }
            return p;
        }) : null);
    }
  };

  const handleCommentClick = async (post: StadiumPost) => {
    setActivePost(post);
    setCommentModalOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
    setComments([]); // 이전 댓글 초기화
    try {
        const data = await fetchNewsComments(post.id, lang);
        setComments(data);
    } catch (error) {
        console.error("Failed to fetch comments:", error);
    }
  };

  const handleCloseCommentModal = () => {
    setIsAnimating(false);
    setTimeout(() => {
        setCommentModalOpen(false);
        setActivePost(null);
        setReplyingTo(null);
        setNewCommentText('');
    }, 500);
  };

  const handlePostComment = async () => {
    if (!user || !activePost || !newCommentText.trim() || isPostingComment) return;
    setIsPostingComment(true);
    try {
        const added = await createNewsComment({
            newsId: activePost.id,
            lang,
            content: newCommentText,
            parentId: replyingTo?.id ? String(replyingTo.id) : null
        });
        setComments(prev => [...prev, added]);
        setNewCommentText('');
        setReplyingTo(null);
        // 해당 포스트의 댓글 수 업데이트
        setPosts(prev => prev ? prev.map(p => p.id === activePost.id ? { ...p, comments_count: p.comments_count + 1 } : p) : null);
    } catch (error) {
        console.error("Failed to post comment:", error);
    } finally {
        setIsPostingComment(false);
    }
  };

  const handleCommentLikeToggle = async (commentId: string | number, isLiked: boolean) => {
    if (!user) return;
    try {
        setComments(prev => prev.map(c => {
            if (c.id === commentId) {
                const currentLikes = Number(c.likes_count) || 0;
                return {
                    ...c,
                    is_liked: !isLiked,
                    likes_count: isLiked ? Math.max(0, currentLikes - 1) : currentLikes + 1
                };
            }
            return c;
        }));

        if (isLiked) await unlikeNewsComment(commentId);
        else await likeNewsComment(commentId);
    } catch (error) {
        console.error("Failed to toggle comment like:", error);
    }
  };

  const handleTeamClick = async (teamId: string) => {
    if (selectedTeamId === teamId) return;
    
    setLocalSelectedTeamId(teamId);
    setPostsLoading(true);
    
    try {
      const fetchedPosts = await communityService.getStadiumPosts(40, [teamId]);
      const visiblePosts = (fetchedPosts || []).filter(post => !post.is_hidden);
      setPosts(visiblePosts);
      // Update URL without full page reload
      window.history.replaceState(null, '', `/community/stadium/${teamId}`);
    } catch (error) {
      console.error("Failed to fetch posts for team:", error);
    } finally {
      setPostsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="animate-spin text-rose-500" size={32} />
      </div>
    );
  }

  const handleNavigateToPlayer = (id: string) => (window as any).navigateTo?.(`/player/${id}`);
  const handleNavigateToNews = (item: any) => (window as any).navigateTo?.(`/feed/item/${item.id}`);

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 relative">

      {/* Mobile Floating Post Button */}
      <div className="fixed bottom-[80px] md:hidden right-4 z-50">
        <button 
          onClick={() => {
            if (!user) {
              (window as any).navigateTo?.('/login');
              return;
            }
            setIsPostModalOpen(true);
          }} 
          className="w-14 h-14 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-2xl shadow-rose-200 active:scale-95 transition-all duration-300 ring-4 ring-white"
        >
          <PenSquare className="w-6 h-6" strokeWidth={2.5} />
        </button>
      </div>

      <StadiumPostModal 
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        teamId={selectedTeamId}
        onPostSuccess={() => loadStadiumData(false)}
      />

      {/* Favorite Teams Horizontal Slider - Always Visible */}
      <div className="bg-white">
          <div className="flex gap-6 overflow-x-auto no-scrollbar pb-2 px-2">
              {favoriteTeams.length > 0 ? (
                  favoriteTeams.map((team) => (
                      <button 
                        key={`fav-team-${team.id}`}
                        onClick={() => handleTeamClick(String(team.id))}
                        className="flex flex-col items-center gap-1.5 shrink-0 group transition-transform active:scale-95"
                      >
                          <div className={`relative p-[3px] rounded-full ${
                              selectedTeamId === String(team.id) 
                              ? 'bg-gradient-to-tr from-amber-400 via-rose-500 to-fuchsia-600' 
                                : 'bg-slate-200'
                          }`}>
                              <div className="w-[66px] h-[66px] rounded-full bg-white p-[3px]">
                                  <div className="w-full h-full rounded-full bg-slate-50 overflow-hidden p-2.5">
                                      <ImageWithFallback 
                                        src={team.logo || team.logo_url} 
                                        alt={getTeamName(String(team.id), lang, team.name)} 
                                        className="w-full h-full object-contain"
                                      />
                                  </div>
                              </div>
                          </div>
                          <span className={`text-[10px] font-medium truncate max-w-[72px] ${hasNewPosts[team.id] && 'ml-1'} ${selectedTeamId === String(team.id) ? 'text-slate-900 font-bold' : 'text-slate-500 group-hover:text-slate-900'}`}>
                              {getTeamName(String(team.id), lang, team.name)}
                              
                              {hasNewPosts[team.id] && <span className='inline-block w-1 h-1 rounded-full bg-rose-500 ml-1 align-top animate-pulse'></span>}
                          </span>
                      </button>
                  ))
              ) : user ? (
                  <div className="w-full py-4 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-50 rounded-2xl">
                      <p className="text-[11px] font-bold text-slate-400">No favorite teams added yet</p>
                      <button 
                        onClick={() => (window as any).navigateTo?.('/games')}
                        className="text-[10px] font-black text-rose-500 uppercase tracking-tighter hover:underline"
                      >
                        Find Teams
                      </button>
                  </div>
              ) : (
                  <div className="flex gap-5 opacity-30 grayscale pointer-events-none">
                      {[1,2,3,4,5].map(i => (
                          <div key={i} className="flex flex-col items-center gap-3 shrink-0">
                              <div className="w-16 h-16 rounded-[22px] bg-slate-100" />
                              <div className="w-10 h-2 bg-slate-100 rounded" />
                          </div>
                      ))}
                  </div>
              )}
          </div>
      </div>

      {/* Confirm and Report Modals for Comments - Moved outside to be on top */}
      <ConfirmModal
        isOpen={!!commentToDelete}
        onClose={() => setCommentToDelete(null)}
        onConfirm={async () => {
          if (commentToDelete) {
            try {
              await deleteNewsComment(commentToDelete);
              setComments(prev => prev.filter(c => c.id !== commentToDelete));
              if (activePost) {
                setPosts(prev => prev ? prev.map(p => p.id === activePost.id ? { ...p, comments_count: Math.max(0, p.comments_count - 1) } : p) : null);
              }
              setReplyingTo(null);
              showToast('success', t(lang, 'deleteSuccess'));
            } catch (err) {
              showToast('error', t(lang, 'deleteFailed'));
            }
          }
          setCommentToDelete(null);
        }}
        title={t(lang, 'deleteConfirm')}
        description={t(lang, 'deleteCommentConfirm')}
        confirmLabel={t(lang, 'delete')}
        cancelLabel={t(lang, 'cancel')}
      />

      {!!commentToReport && (
        <ReportModal
          isOpen={!!commentToReport}
          onClose={() => setCommentToReport(null)}
          onConfirm={async (reason) => {
            try {
              await reportNewsComment(commentToReport, reason);
              showToast('success', t(lang, 'reportSubmitted'));
            } catch (err) {
              showToast('error', t(lang, 'alreadyReported'));
            }
            setCommentToReport(null);
          }}
          lang={lang}
        />
      )}

      {/* Posts Section */}
      <div className={`transition-opacity duration-200 ${postsLoading ? 'opacity-50' : 'opacity-100'}`}>
        {posts === null || (postsLoading && posts.length === 0) ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="animate-spin text-rose-500" size={32} />
          </div>
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <StadiumPostItem 
              key={post.id} 
              post={post} 
              lang={lang} 
              handleLike={handleLike} 
              onCommentClick={() => handleCommentClick(post)}
            />
          ))
        ) : !postsLoading ? (
          <div className="bg-white rounded-[32px] overflow-hidden">
            <EmptyState 
              icon={Users}
              title={t(lang, 'noPostsFound') || 'No posts yet'}
              description={selectedTeamId ? `${getTeamName(selectedTeamId, lang)}에 ${t(lang, 'beTheFirstToPost')}` : t(lang, 'beTheFirstToPost')}
              lang={lang}
            />
          </div>
        ) : (
          <div className="py-20 flex justify-center">
            <Loader2 className="animate-spin text-rose-500" size={32} />
          </div>
        )}
      </div>

      {/* 인스타그램 스타일 댓글 모달 (선수 퀵 모달과 동일한 레이아웃 구조) */}
      {commentModalOpen && activePost && (
          <div className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
              <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={handleCloseCommentModal}></div>
              
              <div className={`relative bg-white w-full sm:max-w-lg h-[75vh] sm:h-auto sm:max-h-[85vh] rounded-t-[40px] sm:rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                  
                  {/* 상단 닫기 버튼 (선수 모달 스타일) */}
                  <div className="absolute top-6 right-6 z-20">
                      <button onClick={handleCloseCommentModal} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all active:scale-90">
                          <X size={20} strokeWidth={3} />
                      </button>
                  </div>

                  <header className="p-8 pb-0 flex flex-col shrink-0">
                      <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{t(lang, 'comments')}</h2>
                  </header>
                  
                  <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-2">
                      {/* 댓글 리스트 */}
                      <div className="pb-24">
                          {comments.length > 0 ? (
                              comments.filter(c => !c.parent_id || c.parent_id === 'null').map(parentComment => (
                                  <div key={parentComment.id} className="flex flex-col">
                                      {/* 부모 댓글 */}
                                      <div className="py-3 flex gap-3 border-b border-slate-50/50">
                                          <div className="relative shrink-0 pt-1">
                                              <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-slate-100">
                                                  <img 
                                                      src={parentComment.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(parentComment.user?.name || 'U')}&background=random`} 
                                                      className="w-full h-full object-cover" 
                                                      alt="" 
                                                  />
                                              </div>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                                  <div className="flex flex-col">
                                                      <div className="flex items-center gap-2 mb-0.5">
                                                          <span className="text-[13px] font-bold text-slate-900">{parentComment.user?.name}</span>
                                                          <span className="text-[11px] text-slate-400 font-medium">{calculateTimeAgo(parentComment.created_at)}</span>
                                                      </div>
                                                      <p className="text-[14px] text-slate-800 leading-snug break-words pr-8">
                                                          {parentComment.content}
                                                      </p>
                                                      <div className="flex items-center gap-4 mt-2.5">
                                                          <button 
                                                            onClick={() => setReplyingTo(parentComment)}
                                                            className="text-[11px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                                                          >
                                                            {t(lang, 'reply')}
                                                          </button>
                                                          
                                                          <button 
                                                              onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  if (!user) { (window as any).navigateTo?.('/login'); return; }
                                                                  setCommentToReport(parentComment.id.toString());
                                                              }}
                                                              className="text-[11px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                                                          >
                                                              {t(lang, 'report')}
                                                          </button>

                                                          {(user?.id === parentComment.user_id || user?.role === 'admin') && (
                                                              <button 
                                                                  onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      setCommentToDelete(parentComment.id);
                                                                  }}
                                                                  className="text-[11px] font-bold text-rose-400 hover:text-rose-500 transition-colors"
                                                              >
                                                                  {t(lang, 'delete')}
                                                              </button>
                                                          )}
                                                      </div>
                                                  </div>
                                          </div>
                                          <div className="flex flex-col items-center gap-1 pt-1 min-w-[32px]">
                                              <button 
                                                onClick={() => handleCommentLikeToggle(parentComment.id, !!parentComment.is_liked)}
                                                className={`transition-colors active:scale-90 ${parentComment.is_liked ? 'text-rose-500' : 'text-slate-300 hover:text-slate-400'}`}
                                              >
                                                  <Heart size={18} fill={parentComment.is_liked ? 'currentColor' : 'none'} strokeWidth={2} />
                                              </button>
                                              {parentComment.likes_count > 0 && <span className={`text-[10px] font-bold ${parentComment.is_liked ? 'text-rose-500' : 'text-slate-400'}`}>{parentComment.likes_count}</span>}
                                          </div>
                                      </div>

                                      {/* 대댓글 리스트 (들여쓰기 적용) */}
                                      {comments.some(child => child.parent_id && String(child.parent_id) === String(parentComment.id)) && (
                                          <div className="pl-12">
                                              {!expandedParents[parentComment.id] ? (
                                                  <button 
                                                    onClick={() => setExpandedParents(prev => ({ ...prev, [parentComment.id]: true }))}
                                                    className="py-3 flex items-center gap-3 text-slate-400 hover:text-slate-600 transition-colors"
                                                  >
                                                      <div className="w-8 h-[1px] bg-slate-100" />
                                                      <span className="text-[11px] font-bold uppercase tracking-tight">
                                                          {t(lang, 'viewReplies') || '답글 보기'} ({comments.filter(child => child.parent_id && String(child.parent_id) === String(parentComment.id)).length})
                                                      </span>
                                                  </button>
                                              ) : (
                                                  <>
                                                      {comments.filter(child => child.parent_id && String(child.parent_id) === String(parentComment.id)).map(childComment => (
                                                          <div key={childComment.id} className="py-2.5 flex gap-3">
                                                              <div className="relative shrink-0 pt-1">
                                                                  <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-slate-100">
                                                                      <img 
                                                                          src={childComment.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(childComment.user?.name || 'U')}&background=random`} 
                                                                          className="w-full h-full object-cover" 
                                                                          alt="" 
                                                                      />
                                                                  </div>
                                                              </div>
                                                              <div className="flex-1 min-w-0">
                                                                  <div className="flex flex-col">
                                                                      <div className="flex items-center gap-2 mb-0.5">
                                                                          <span className="text-[12px] font-bold text-slate-900">{childComment.user?.name}</span>
                                                                          <span className="text-[10px] text-slate-400 font-medium">{calculateTimeAgo(childComment.created_at)}</span>
                                                                      </div>
                                                                      <p className="text-[13px] text-slate-700 leading-snug break-words">
                                                                          <span className="text-rose-500 font-bold mr-1">@{parentComment.user?.name}</span>
                                                                          {childComment.content}
                                                                      </p>
                                                                      <div className="flex items-center gap-4 mt-2">
                                                                          <button 
                                                                            onClick={() => setReplyingTo(parentComment)}
                                                                            className="text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                                                                          >
                                                                            {t(lang, 'reply')}
                                                                          </button>
                                                                          
                                                                          <button 
                                                                              onClick={(e) => {
                                                                                  e.stopPropagation();
                                                                                  if (!user) { (window as any).navigateTo?.('/login'); return; }
                                                                                  setCommentToReport(childComment.id.toString());
                                                                              }}
                                                                              className="text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                                                                          >
                                                                              {t(lang, 'report')}
                                                                          </button>

                                                                          {(user?.id === childComment.user_id || user?.role === 'admin') && (
                                                                              <button 
                                                                                  onClick={(e) => {
                                                                                      e.stopPropagation();
                                                                                      setCommentToDelete(childComment.id);
                                                                                  }}
                                                                                  className="text-[10px] font-bold text-rose-400 hover:text-rose-500 transition-colors"
                                                                              >
                                                                                  {t(lang, 'delete')}
                                                                              </button>
                                                                          )}
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                              <div className="flex flex-col items-center gap-1 pt-1 min-w-[28px]">
                                                                  <button 
                                                                    onClick={() => handleCommentLikeToggle(childComment.id, !!childComment.is_liked)}
                                                                    className={`transition-colors active:scale-90 ${childComment.is_liked ? 'text-rose-500' : 'text-slate-300 hover:text-slate-400'}`}
                                                                  >
                                                                      <Heart size={14} fill={childComment.is_liked ? 'currentColor' : 'none'} strokeWidth={2} />
                                                                  </button>
                                                                  {childComment.likes_count > 0 && <span className={`text-[9px] font-bold ${childComment.is_liked ? 'text-rose-500' : 'text-slate-400'}`}>{childComment.likes_count}</span>}
                                                              </div>
                                                          </div>
                                                      ))}
                                                      <button 
                                                        onClick={() => setExpandedParents(prev => ({ ...prev, [parentComment.id]: false }))}
                                                        className="py-2 flex items-center gap-3 text-slate-400 hover:text-slate-600 transition-colors"
                                                      >
                                                          <div className="w-8 h-[1px] bg-slate-100" />
                                                          <span className="text-[11px] font-bold uppercase tracking-tight">
                                                              {t(lang, 'hideReplies') || '답글 숨기기'}
                                                          </span>
                                                      </button>
                                                  </>
                                              )}
                                          </div>
                                      )}
                                  </div>
                              ))
                          ) : (
                              <div className="py-20 text-center">
                                  <MessageCircle size={40} className="mx-auto text-slate-100 mb-3" />
                                  <p className="text-xs font-bold text-slate-300 uppercase tracking-widest">{t(lang, 'noCommentsYet')}</p>
                              </div>
                          )}
                      </div>
                  </div>

                  {/* 댓글 입력창 (모달 하단 고정) */}
                  <div className="shrink-0 pb-safe bg-white border-t border-slate-100 flex flex-col">
                      {/* 이모티콘 리액션 바 */}
                      <div className="flex items-center gap-5 overflow-x-auto no-scrollbar px-6 py-4 pb-0">
                          {['❤️', '🙌', '🔥', '👏', '😢', '😍', '😮', '😂', '⚽️', '🥅', '👟', '🏆', '🤩', '👍', '🙏', '💯', '🤔', '😲'].map((emoji) => (
                              <button 
                                key={emoji}
                                onClick={() => setNewCommentText(prev => prev + emoji)}
                                className="text-2xl hover:scale-125 transition-transform active:scale-95 shrink-0"
                              >
                                  {emoji}
                              </button>
                          ))}
                      </div>

                      <div className="p-6 flex flex-col gap-3">
                        {replyingTo && (
                          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl border border-slate-100 animate-in slide-in-from-bottom-2">
                              <div className="flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                  <span className="text-[11px] font-bold text-slate-500">
                                      <span className="text-slate-900">{replyingTo.user?.name}</span>님에게 답글 남기는 중...
                                  </span>
                              </div>
                              <button 
                                  onClick={() => setReplyingTo(null)}
                                  className="p-1 text-slate-300 hover:text-slate-900 transition-all"
                              >
                                  <X size={14} strokeWidth={3} />
                              </button>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                              <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-100">
                                  <img src={user?.avatar || `https://ui-avatars.com/api/?name=Me&background=random`} className="w-full h-full object-cover" alt="" />
                              </div>
                              {favoriteTeams.length > 0 && (
                                  <div className="absolute top-5 -right-1 w-4 h-4 bg-white rounded-full p-[1.5px] shadow-sm border border-slate-100">
                                      <ImageWithFallback 
                                          src={favoriteTeams[0].logo || favoriteTeams[0].logo_url}
                                          alt=""
                                          className="w-full h-full object-contain"
                                      />
                                  </div>
                              )}
                          </div>
                          <div className="flex-1 relative">
                              <input 
                                  type="text" 
                                  value={newCommentText}
                                  onChange={(e) => setNewCommentText(e.target.value)}
                                  placeholder={replyingTo ? `@${replyingTo.user?.name}님에게 답글...` : t(lang, 'addAComment')}
                                  className="w-full bg-slate-50 border-none rounded-full py-2.5 px-5 pr-12 text-[13px] font-medium text-slate-900 focus:ring-2 focus:ring-rose-500/10 outline-none transition-all"
                                  onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                          handlePostComment();
                                      }
                                  }}
                              />
                              <button 
                                  onClick={handlePostComment}
                                  disabled={!newCommentText.trim() || isPostingComment}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-rose-500 text-white rounded-full disabled:opacity-30 shadow-lg shadow-rose-100 transition-all active:scale-90"
                              >
                                  {isPostingComment ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />}
                              </button>
                          </div>
                        </div>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};
